import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faCheckCircle,
  faInfoCircle,
  faFillDrip
} from '@fortawesome/free-solid-svg-icons';

/**
 * RepairProcessTable Component
 * 
 * Displays repair project spans with inspection data and repair processes
 * Similar UI to SimulationResultsTable for consistency
 */
const RepairProcessTable = ({ 
  spans = [],
  onManagePanelBays,
  projectStatus,
  searchTerm = '',
  filterStatus = 'all'
}) => {
  const handleManagePanelBays = (span) => {
    if (typeof onManagePanelBays === 'function') {
      onManagePanelBays(span);
    }
  };

  const [expandedSpans, setExpandedSpans] = useState(new Set());

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (value, decimals = 2) => {
    return new Intl.NumberFormat('en-CA', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-CA');
  };

  const toggleSpanExpansion = (spanId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setExpandedSpans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(spanId)) {
        newSet.delete(spanId);
      } else {
        newSet.add(spanId);
      }
      return newSet;
    });
  };

  const matchesSearch = (element) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    // Support both new and old field names
    const code = element.element_code || element.span_code || '';
    const name = element.element_name || element.span_name || '';
    const parent = element.parent_name || element.frame_name || '';
    return (
      code.toLowerCase().includes(search) ||
      name.toLowerCase().includes(search) ||
      parent.toLowerCase().includes(search)
    );
  };

  const matchesFilter = (element) => {
    if (filterStatus === 'all') return true;
    // Support both new and old field names
    const status = element.element_status || element.span_status || 'pending';
    return status === filterStatus;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: faInfoCircle, text: 'Pending' },
      'completed': { color: 'bg-green-100 text-green-800', icon: faCheckCircle, text: 'Completed' }
    };
    
    const badge = badges[status] || badges['pending'];
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        <FontAwesomeIcon icon={badge.icon} className="w-3 h-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const badges = {
      'critical': { color: 'bg-red-100 text-red-800', text: 'Critical' },
      'high': { color: 'bg-orange-100 text-orange-800', text: 'High' },
      'medium': { color: 'bg-yellow-100 text-yellow-800', text: 'Medium' },
      'low': { color: 'bg-green-100 text-green-800', text: 'Low' }
    };
    
    const badge = badges[urgency] || badges['medium'];
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const filteredSpans = spans.filter(span => matchesSearch(span) && matchesFilter(span));

  if (spans.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No spans in this repair project
      </div>
    );
  }

  if (filteredSpans.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No spans match your search or filter criteria
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredSpans.map((span) => {
        const isExpanded = expandedSpans.has(span.id);
        // Allow panel bay management for pending projects (before completion)
        const canManagePanelBays = projectStatus === 'pending' && typeof onManagePanelBays === 'function';

        return (
          <div key={span.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {/* Span Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={(e) => toggleSpanExpansion(span.id, e)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    type="button"
                  >
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronDown : faChevronRight}
                      className="w-4 h-4"
                    />
                  </button>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{span.element_name || span.span_name}</h3>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusBadge(span.element_status || span.span_status || 'pending')}
                  {canManagePanelBays && (
                    <button
                      onClick={() => handleManagePanelBays(span)}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200 hover:bg-blue-100 transition-colors flex items-center gap-1"
                      type="button"
                      title="Manage panel bay painting data"
                    >
                      <FontAwesomeIcon icon={faFillDrip} />
                      Panel Bays
                    </button>
                  )}
                  {/* Individual element completion removed - elements are completed when project is completed */}
                </div>
              </div>
            </div>

            {/* Span Details - Always Visible */}
            <div className="px-4 py-3 bg-white">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Total Area</p>
                  <p className="text-sm font-medium text-gray-900">{formatNumber(span.total_area)} m²</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Parent</p>
                  <p className="text-sm font-medium text-gray-900">{span.parent_name || span.frame_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Corrosion</p>
                  <p className="text-sm font-medium text-gray-900">
                    {span.inspection_corrosion_category || span.corrosion_category || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Damage Level</p>
                  <p className="text-sm font-medium text-gray-900">
                    {span.inspection_damage_severity || span.damage_level || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-200">
                {/* Inspection Data */}
                {span.inspection_id && (
                  <div className="px-4 py-3 bg-blue-50">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                      Inspection Data
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-blue-700">Inspection Date</p>
                        <p className="text-sm font-medium text-blue-900">
                          {formatDate(span.inspection_date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-700">Inspector</p>
                        <p className="text-sm font-medium text-blue-900">
                          {span.inspector_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-700">Damage Area</p>
                        <p className="text-sm font-medium text-blue-900">
                          {span.inspection_damage_area ? `${formatNumber(span.inspection_damage_area)} m²` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-700">Urgency</p>
                        <div className="mt-1">
                          {span.urgency_level ? getUrgencyBadge(span.urgency_level) : 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Damage Split */}
                    <div className="mt-3">
                      <p className="text-xs text-blue-700 mb-2">Damage Split</p>
                      <div className="flex space-x-4">
                        <div className="flex items-center">
                          <span className="text-xs text-blue-700 mr-2">A:</span>
                          <span className="text-sm font-medium text-blue-900">
                            {span.damage_percentage_a || 70}%
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs text-blue-700 mr-2">B:</span>
                          <span className="text-sm font-medium text-blue-900">
                            {span.damage_percentage_b || 20}%
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs text-blue-700 mr-2">C:</span>
                          <span className="text-sm font-medium text-blue-900">
                            {span.damage_percentage_c || 10}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {span.recommendations && (
                      <div className="mt-3">
                        <p className="text-xs text-blue-700">Recommendations</p>
                        <p className="text-sm text-blue-900 mt-1">{span.recommendations}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Repair Processes */}
                <div className="px-4 py-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Repair Processes Required
                  </h4>
                  {span.repair_processes && span.repair_processes.length > 0 ? (
                    <div className="space-y-2">
                      {span.repair_processes.map((process, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-700">
                              {idx + 1}. {process.process_name}
                            </span>
                            {process.is_required && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                                Required
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            Est: {formatCurrency(parseFloat(process.unit_cost || 0) * parseFloat(span.total_area || 0))}
                          </div>
                        </div>
                      ))}
                      <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-900">Total Estimated Cost:</span>
                        <span className="text-lg font-bold text-green-600">
                          {formatCurrency(span.estimated_cost || 0)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No repair processes defined</p>
                  )}
                </div>

                {/* Completion Notes */}
                {span.span_notes && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <p className="text-xs text-gray-500">Completion Notes</p>
                    <p className="text-sm text-gray-900 mt-1">{span.span_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RepairProcessTable;





